import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import ResilientImage from "../ResilientImage/ResilientImage";
import "./PhotoOverlay.css";
import { useTranslation } from "../../i18n";

const PhotoOverlay = ({ src, title, link, priority = false }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [showOverlay, setShowOverlay] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const overlayRef = useRef(null);
    const showTimerRef = useRef(null);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (!isMobile) return;

        const clearShowTimer = () => {
            if (showTimerRef.current) {
                clearTimeout(showTimerRef.current);
                showTimerRef.current = null;
            }
        };

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    clearShowTimer();
                    showTimerRef.current = setTimeout(() => {
                        setShowOverlay(true);
                    }, 2000);
                } else {
                    clearShowTimer();
                    setShowOverlay(false);
                }
            },
            { threshold: 1.0 }
        );

        if (overlayRef.current) {
            observer.observe(overlayRef.current);
        }

        return () => {
            clearShowTimer();
            observer.disconnect();
        };
    }, [isMobile]);

    return (
        <div
            onClick={() => {navigate(link)}}
            ref={overlayRef}
            className="photo-overlay"
            onMouseEnter={() => !isMobile && setShowOverlay(true)}
            onMouseLeave={() => !isMobile && setShowOverlay(false)}
        >
            <ResilientImage
                src={src}
                alt={title}
                className="photo-overlay__image"
                loading={priority ? "eager" : "lazy"}
                decoding="async"
                fetchPriority={priority ? "high" : "auto"}
            />
            {showOverlay && (
                <motion.div
                    className="photo-overlay__mask"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <h3 className="photo-overlay__title">{title}</h3>
                    <span className="photo-overlay__label">{t("common.view")}</span>
                </motion.div>
            )}
        </div>
    );
};

export default PhotoOverlay;
